import React from 'react';
import { useSEO } from '@/hooks/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Target,
  Heart,
  HelpCircle,
  BookOpen,
  Mail,
  MessageSquare,
  Globe,
  Rocket,
  GraduationCap,
  BarChart3,
  ArrowRight,
} from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

const supportItems = [
  {
    title: 'Help Center',
    description: 'Browse FAQs, guides, and tutorials to get the most out of Five AI.',
    icon: BookOpen,
    href: '/coach',
    cta: 'Get Help',
    gradient: 'from-emerald-500/10 to-teal-500/10',
    iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  },
  {
    title: 'AI Coach',
    description: 'Your AI career coach is always available. Chat for personalized guidance, tips, and support.',
    icon: MessageSquare,
    href: '/coach',
    cta: 'Talk to Coach',
    gradient: 'from-primary/10 to-violet-500/10',
    iconBg: 'bg-primary/15 text-primary',
  },
  {
    title: 'Contact Us',
    description: 'Reach out for support, feedback, or partnership inquiries.',
    icon: Mail,
    href: 'mailto:support@fiveai.co',
    cta: 'Email Support',
    external: true,
    gradient: 'from-amber-500/10 to-orange-500/10',
    iconBg: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  },
];

const whatWeDo = [
  {
    title: 'Career transformation',
    description: 'We help you level up with AI-powered coaching, learning paths, and actionable nudges.',
    icon: Rocket,
    gradient: 'from-primary/10 to-violet-500/10',
    iconBg: 'bg-primary/15 text-primary',
  },
  {
    title: 'Personalized learning',
    description: 'Daily lessons, goals, and progress tracking tailored to your objectives.',
    icon: GraduationCap,
    gradient: 'from-emerald-500/10 to-teal-500/10',
    iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  },
  {
    title: 'Measurable results',
    description: 'We focus on outcomes: skills gained, goals reached, and career growth.',
    icon: BarChart3,
    gradient: 'from-blue-500/10 to-cyan-500/10',
    iconBg: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  },
];

export const AboutPage: React.FC = () => {
  useSEO({ title: 'About Five AI - Our Mission & Team', description: 'Five AI democratizes career growth with AI coaching and personalized micro-learning. Built by UnboundXLabs in Bangalore, India. Learn about our mission, team, and how we help professionals level up.', path: '/about' });
  const navigate = useNavigate();

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="About Five AI">
      <Header />
      <div className="pt-16">
        {/* Hero — aligned with Features */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
              >
                <Heart className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Our mission</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">We're building</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                  career transformation for everyone
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                Five AI combines AI coaching, personalized learning, and daily nudges so you can achieve measurable career growth—faster.
              </p>
              <p className="text-sm text-muted-foreground/80">
                Democratizing access to world-class career guidance
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5 }}
              className="flex flex-col md:flex-row gap-12 items-center"
            >
              <div className="flex-1">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0 shadow-sm">
                    <Target className="w-7 h-7" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-primary uppercase tracking-wider">Mission</span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mt-1">
                      Democratize career growth with AI
                    </h2>
                  </div>
                </div>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  We believe everyone deserves access to world-class career guidance. Five AI brings AI-powered coaching, personalized learning plans, and daily motivation—so you can reach your goals without expensive consultants or endless self-help books.
                </p>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="w-64 h-64 rounded-2xl bg-gradient-to-br from-primary/20 via-violet-500/15 to-fuchsia-500/10 flex items-center justify-center border-2 border-primary/10 shadow-lg">
                  <Globe className="w-24 h-24 text-primary/50" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* What we do — feature-style cards */}
        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8"
            >
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">What we do</h2>
                <p className="text-muted-foreground mt-1 max-w-xl">
                  We combine AI, learning science, and career expertise to deliver results you can measure.
                </p>
              </div>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6">
              {whatWeDo.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <Card
                    hover
                    className={`h-full p-6 sm:p-8 rounded-2xl border-2 bg-gradient-to-br ${item.gradient} hover:border-primary/40 transition-all duration-300 group overflow-hidden`}
                  >
                    <div className={`w-11 h-11 rounded-xl ${item.iconBg} flex items-center justify-center mb-5 shrink-0`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Support & Help */}
        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 w-fit">
                <HelpCircle className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Support</span>
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">We're here to help</h2>
                <p className="text-muted-foreground mt-1 max-w-xl">
                  Whether you need guidance, have questions, or want to share feedback—we've got you covered.
                </p>
              </div>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6">
              {supportItems.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <Card
                    hover
                    className={`h-full p-6 sm:p-8 rounded-2xl border-2 flex flex-col bg-gradient-to-br ${item.gradient} hover:border-primary/40 transition-all duration-300 group overflow-hidden`}
                  >
                    <div className={`w-11 h-11 rounded-xl ${item.iconBg} flex items-center justify-center mb-5 shrink-0`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground flex-1 mb-6 leading-relaxed">{item.description}</p>
                    {item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                      >
                        {item.cta}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                      >
                        {item.cta}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    )}
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA — aligned with Features */}
        <section className="relative py-20 sm:py-24 border-t border-border">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent dark:via-primary/[0.06]" />
          <div className="container relative mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Ready to grow?
              </h2>
              <p className="text-muted-foreground mb-8 text-lg">
                Start your journey with the AI Coach or explore our features.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/onboarding')}
                  className="rounded-xl gap-2 shadow-lg shadow-primary/20"
                >
                  <Sparkles className="w-5 h-5" />
                  Get Started
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/features')}
                  className="rounded-xl gap-2 border-2"
                >
                  View Features
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
